import { createSlice } from "@reduxjs/toolkit";

/**
 * @name @createSlice
 * @description create redux store slice for Appointment
 **/

/**
 * Helper function to filter appointments based on switch type and selected types
 * @param {Array} appointments - All appointments
 * @param {string} switchType - "serviceTypes" or "members"
 * @param {Array} appointmentType - Selected type IDs
 * @returns {Array} Filtered appointments
 */
const filterAppointments = (appointments, switchType, appointmentType, serviceTypes = []) => {
  if (!appointmentType?.length) return [];
  let filtered = [];
  if (switchType === "serviceTypes") {
    filtered = appointments.filter((event) => {
      if (event.isBusy) {
        // Find if the member of the busy slot is in any of the selected serviceTypes/calendars
        const selectedCalendars = serviceTypes.filter((s) =>
          appointmentType.includes(s.calendarId || s.uuid || s.id)
        );
        return selectedCalendars.some((cal) =>
          cal.members?.some(
            (m) =>
              (m.id || m.participantId || m.memberId) === event.memberId ||
              (m.id || m.participantId || m.memberId) === event.participantId
          )
        );
      }
      return appointmentType.includes(event.calendarId);
    });
  } else {
    // switchType === "members"
    filtered = appointments.filter((event) =>
      appointmentType.includes(event.memberId || event.participantId)
    );
  }

  // Prioritize booked events (isBusy is false/undefined) over busy slots (isBusy is true)
  return filtered.sort((a, b) => {
    const aBusy = !!a.isBusy;
    const bBusy = !!b.isBusy;
    if (aBusy && !bBusy) return 1;
    if (!aBusy && bBusy) return -1;
    return 0;
  });
};

import OutlookIcon from "@/public/assets/app-images/outlook-icon.svg";
import GoogleIcon from "@/public/assets/app-images/google-icon.svg";
import { Enums, GroupByEnums, LocationType } from "@/services/dropdown-enums";

const platforms = [
  {
    id: Enums.EmailProviderType.microsoft,
    value: "MICROSOFT",
    label: "Outlook",
    name: "outlook",
    icon: OutlookIcon.src,
  },
  {
    id: Enums.EmailProviderType.google,
    value: "GMAIL",
    label: "Google",
    name: "google",
    icon: GoogleIcon.src,
  },
];

const InitialBookingFields = [
  {
    fieldLabel: "First Name",
    fieldKey: "FirstName",
    fieldToolTipText: "",
    isRequired: true,
    sortOrder: 0,
    calendarId: 0,
    isMandatory: true,
  },
  {
    fieldLabel: "Last Name",
    fieldKey: "LastName",
    fieldToolTipText: "",
    isRequired: true,
    sortOrder: 0,
    calendarId: 0,
    isMandatory: true,
  },
  {
    fieldLabel: "Email",
    fieldKey: "Email",
    fieldToolTipText: "",
    isRequired: true,
    sortOrder: 0,
    calendarId: 0,
    isMandatory: true,
  },
];
export const initialEventDetailsState = {
  // id: 0,
  // uuid: 0,
  openingHours: [],
  name: "",
  timeZoneId: "",
  description: "",
  bookingPageTitle: "Personal Information",
  assignmentType: Enums.ScheduleServiceTypes.RoundRobin,
  duration: 45,
  durationUnit: GroupByEnums.minute,
  minimumBookingNotice: 2,
  minimumBookingNoticeUnit: GroupByEnums.hour,
  minimumCancelationNotice: 3,
  minimumCancelationNoticeUnit: GroupByEnums.hour,
  futureLimit: 2,
  futureLimitUnit: GroupByEnums.week,
  bufferTime: 30,
  bufferTimeUnit: GroupByEnums.minute,
  bookingLimit: 1,
  members: [],
  reponseHandled: false,
  reminderChannelStatuses: [
    { channelType: Enums.CustomerChannels.InAppNotifications },
  ],
  appointmentReminders: [],
  isReminderUpdate: true,
  appointmentUserDefinedFields: [...InitialBookingFields],
  deletedCustomFieldIds: [],
  logoUrl: "",
  color: "#0068D3",
  themeId: 0,
  logoImage: null,
  appointmentLocations: [{ type: LocationType.Physical, value: "TBD" }],
  ccEmail: [],
  bccEmail: [],
};

export const AppointmentSlice = createSlice({
  name: "appointment",
  initialState: {
    appointments: [],
    filteredAppointments: [],
    selectedAppointment: null,
    appointmentType: [],
    serviceTypes: [],
    members: [],
    loading: false,
    switchType: "serviceTypes",
    switchView: "calendar",
    platforms: platforms,
    settingsActiveTab: 1,
    appointmentSettings: {},
    eventDetails: initialEventDetailsState,
    calendars: [],
    isCalendarsFetched: false,
    calendar: {},
    tempStatusPayload: null,
    selectedDate: Date(),
    calendarView: "month",
    bookingError: "",

    // ── Persisted view state ──
    calendarView: "month", // "day" | "week" | "month"

    // ── Persisted filter selections (per switch-type) ──
    selectedServiceTypeIds: null,
    selectedMemberIds: null,

    // ── Form-draft state ──
    unsavedFormData: null, // For appointments tab
    unsavedLeadFormData: null, // For lead details (single draft, not per-lead)
    savedInfoWhenLeadCreate: null,
    pendingAppointmentModal: false,
    appointmentModalShowNewLeadForm: false,
    appointmentInlineLeadDraft: null,

    // ── Appointment settings modal tab search fields ──
    settingsModalMembersSearch: "",

    // ── Appointment settings page-layout open/form state (isPageLayout=true only) ──
    appointmentSettingsOpen: false,      // whether the settings form is open on the page layout
    appointmentSettingsEditData: null,   // the record being edited (null = new appointment)
    appointmentSettingsFormDraft: null,  // { calendarId: string|null, values: formikValues }
  },

  reducers: {
    setLoadingToState: (state, action) => {
      state.loading = action.payload;
    },
    setAppointmentsToState: (state, action) => {
      state.appointments = action.payload;
      // Re-apply current filters to the new appointments
      state.filteredAppointments = filterAppointments(
        action.payload,
        state.switchType,
        state.appointmentType,
        state.serviceTypes
      );
    },
    setTempStatusPayload: (state, action) => {
      state.tempStatusPayload = action.payload;
    },
    setAppointmentToState: (state, action) => {
      let allAppointments = [...state.appointments];
      allAppointments.push(action.payload);
      state.appointments = allAppointments;
      // Re-apply current filters to include the new appointment if it matches
      state.filteredAppointments = filterAppointments(
        allAppointments,
        state.switchType,
        state.appointmentType,
        state.serviceTypes
      );
    },
    updateAppointmentToState: (state, action) => {
      let data = action.payload;
      let allAppointments = JSON.parse(JSON.stringify(state.appointments));
      let index = allAppointments.findIndex((item) => item?.id === data?.id);
      if (index !== -1) {
        allAppointments[index] = data;
      }

      state.appointments = allAppointments;
    },
    removeAppointment: (state, action) => {
      let { id } = action.payload;
      let allAppointments = JSON.parse(JSON.stringify(state.appointments));
      allAppointments = allAppointments.filter((item) => item?.id !== id);
      state.appointments = allAppointments;
      // Re-apply current filters after removing the appointment
      state.filteredAppointments = filterAppointments(
        allAppointments,
        state.switchType,
        state.appointmentType,
        state.serviceTypes
      );
    },
    setSelectedAppointmentToState: (state, action) => {
      state.selectedAppointment = action.payload;
    },
    setServiceTypesToState: (state, action) => {
      state.serviceTypes = action.payload;
    },
    setMembersToState: (state, action) => {
      state.members = action.payload;
    },
    setAppointmentTypeToState: (state, action) => {
      const type = action.payload || [];
      state.appointmentType = type;
      if (state.switchType === "serviceTypes") {
        state.selectedServiceTypeIds = type;
      } else {
        state.selectedMemberIds = type;
      }
      state.filteredAppointments = filterAppointments(
        state.appointments,
        state.switchType,
        type,
        state.serviceTypes
      );
    },
    setSwitchTypeToState: (state, action) => {
      state.switchType = action.payload;
      // Swap appointmentType to the persisted selection for the new type
      const savedIds =
        action.payload === "serviceTypes"
          ? state.selectedServiceTypeIds
          : state.selectedMemberIds;
      state.appointmentType = savedIds ?? [];
      // Re-apply filters based on the new switch type
      state.filteredAppointments = filterAppointments(
        state.appointments,
        action.payload,
        state.appointmentType,
        state.serviceTypes
      );
    },
    setSwitchViewToState: (state, action) => {
      state.switchView = action.payload;
    },
    setCalendarViewToState: (state, action) => {
      state.calendarView = action.payload;
    },
    setSelectedDateToState: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSettingsActiveTab: (state, action) => {
      state.settingsActiveTab = action.payload;
    },
    setAppointmentSettings: (state, action) => {
      const { key = "", value = null } = action.payload;
      if (key && value) {
        state.appointmentSettings[key] = value;
      } else {
        state.appointmentSettings = action.payload;
      }
    },
    setCalendarsToState: (state, action) => {
      state.calendars = action.payload;
    },
    removeCalendar: (state, action) => {
      const calendarId = action.payload;
      state.calendars = state.calendars.filter(
        (c) => c.uuid !== calendarId && c.calendarId !== calendarId && c.id !== calendarId
      );
    },
    setCalendarsIsFetchedToState: (state, action) => {
      state.isCalendarsFetched = action.payload;
    },
    setCalendarToState: (state, action) => {
      state.calendar = action.payload;
    },
    setReponseHandled: (state, action) => {
      state.reponseHandled = action.payload;
    },
    setAppointmentSliceInitialState: (state) => {
      state.calendars = [];
      state.calendar = {};
      state.members = [];
      state.serviceTypes = [];
      state.appointments = [];
      state.filteredAppointments = [];
      state.selectedAppointment = {};
      state.isCalendarsFetched = false;
      // Reset persisted view state
      state.calendarView = "month";
      state.selectedServiceTypeIds = null;
      state.selectedMemberIds = null;
      state.unsavedFormData = null;
      state.appointmentSettingsOpen = false;
      state.appointmentSettingsEditData = null;
      state.appointmentSettingsFormDraft = null;
    },
    setBookingErrorToState: (state, action) => {
      state.bookingError = action.payload;
    },

    // ── Persisted view reducers ──
    setCalendarView: (state, action) => {
      state.calendarView = action.payload;
    },
    setSelectedServiceTypeIds: (state, action) => {
      state.selectedServiceTypeIds = action.payload;
    },
    setSelectedMemberIds: (state, action) => {
      state.selectedMemberIds = action.payload;
    },
    setUnsavedFormData: (state, action) => {
      state.unsavedFormData = action.payload;
    },
    clearFormDraft: (state) => {
      state.unsavedFormData = null;
    },
    setUnsavedLeadFormData: (state, action) => {
      state.unsavedLeadFormData = action.payload;
    },
    clearLeadFormDraft: (state) => {
      state.unsavedLeadFormData = null;
    },
    setSavedInfoWhenLeadCreate: (state, action) => {
      state.savedInfoWhenLeadCreate = action.payload;
    },
    setPendingAppointmentModal: (state, action) => {
      state.pendingAppointmentModal = action.payload;
    },
    setAppointmentModalShowNewLeadForm: (state, action) => {
      state.appointmentModalShowNewLeadForm = action.payload;
    },
    clearPendingAppointmentModalState: (state) => {
      state.pendingAppointmentModal = false;
      state.appointmentModalShowNewLeadForm = false;
    },
    setAppointmentInlineLeadDraft: (state, action) => {
      state.appointmentInlineLeadDraft = action.payload;
    },
    clearAppointmentInlineLeadDraft: (state) => {
      state.appointmentInlineLeadDraft = null;
    },
    clearAppointmentDraftsForDeletedLeads: (state, action) => {
      const leadIds = new Set(
        (action.payload || []).map((id) => Number(id)).filter(Boolean),
      );
      if (!leadIds.size) return;

      const matches = (id) => id != null && leadIds.has(Number(id));
      let shouldClearInline = false;

      const stripLeadFromDraft = (draft) => {
        if (!draft) return draft;
        const leadMatches =
          matches(draft.formValues?.leadId) || matches(draft.savedLead?.id);
        if (!leadMatches) return draft;
        shouldClearInline = true;
        const next = {
          ...draft,
          savedLead: null,
          showInlineLeadForm: false,
        };
        delete next.inlineLeadDraft;
        if (next.formValues) {
          next.formValues = { ...next.formValues, leadId: "" };
        }
        return next;
      };

      if (state.unsavedFormData) {
        state.unsavedFormData = stripLeadFromDraft(state.unsavedFormData);
      }
      if (state.unsavedLeadFormData) {
        state.unsavedLeadFormData = stripLeadFromDraft(
          state.unsavedLeadFormData,
        );
      }
      if (
        state.savedInfoWhenLeadCreate &&
        matches(state.savedInfoWhenLeadCreate.leadId)
      ) {
        state.savedInfoWhenLeadCreate = {
          ...state.savedInfoWhenLeadCreate,
          leadId: "",
        };
        shouldClearInline = true;
      }
      if (shouldClearInline) {
        state.appointmentInlineLeadDraft = null;
      }
    },
    setSettingsModalMembersSearch: (state, action) => {
      state.settingsModalMembersSearch = action.payload;
    },
    clearSettingsModalMembersSearch: (state) => {
      state.settingsModalMembersSearch = "";
    },

    // ── Appointment settings page-layout open/form reducers ──
    setAppointmentSettingsOpen: (state, action) => {
      state.appointmentSettingsOpen = action.payload;
    },
    setAppointmentSettingsEditData: (state, action) => {
      state.appointmentSettingsEditData = action.payload;
    },
    setAppointmentSettingsFormDraft: (state, action) => {
      state.appointmentSettingsFormDraft = action.payload;
    },
    clearAppointmentSettingsFormDraft: (state) => {
      state.appointmentSettingsFormDraft = null;
    },
  },
});

export const {
  setLoadingToState,
  setAppointmentsToState,
  setAppointmentToState,
  updateAppointmentToState,
  removeAppointment,
  setSelectedAppointmentToState,
  setServiceTypesToState,
  setMembersToState,
  setAppointmentTypeToState,
  setSwitchTypeToState,
  setSwitchViewToState,
  setCalendarViewToState,
  setSelectedDateToState,
  setSettingsActiveTab,
  setAppointmentSettings,
  setCalendarsToState,
  removeCalendar,
  setCalendarToState,
  setCalendarsIsFetchedToState,
  setReponseHandled,
  setAppointmentSliceInitialState,
  setTempStatusPayload,
  setBookingErrorToState,
  // Persisted view actions
  setCalendarView,
  setSelectedServiceTypeIds,
  setSelectedMemberIds,
  setUnsavedFormData,
  clearFormDraft,
  setUnsavedLeadFormData,
  clearLeadFormDraft,
  setSavedInfoWhenLeadCreate,
  setPendingAppointmentModal,
  setAppointmentModalShowNewLeadForm,
  clearPendingAppointmentModalState,
  setAppointmentInlineLeadDraft,
  clearAppointmentInlineLeadDraft,
  clearAppointmentDraftsForDeletedLeads,
  setSettingsModalMembersSearch,
  clearSettingsModalMembersSearch,
  setAppointmentSettingsOpen,
  setAppointmentSettingsEditData,
  setAppointmentSettingsFormDraft,
  clearAppointmentSettingsFormDraft,
} = AppointmentSlice.actions;

/* called a selector and allows to select values from the state */

export const getBookingError = (state) => {
  return state.appointment.bookingError;
};

export const getLoading = (state) => {
  return state.appointment.loading;
};

export const getAppointments = (state) => {
  return state.appointment.appointments;
};

export const getSelectedAppointment = (state) => {
  return state.appointment.selectedAppointment;
};

export const getServiceTypes = (state) => {
  return state.appointment.serviceTypes;
};

export const getMembers = (state) => {
  return state.appointment.members;
};
export const getFilteredAppointments = (state) => {
  return state.appointment.filteredAppointments;
};

export const getAppointmentType = (state) => {
  return state.appointment.appointmentType;
};
export const getSwitchType = (state) => {
  return state.appointment.switchType;
};
export const getSwitchView = (state) => {
  return state.appointment.switchView;
};
export const getSelectedDate = (state) => {
  return state.appointment.selectedDate;
};
export const getPlatforms = (state) => {
  return state.appointment.platforms;
};
export const getSettingsActiveTab = (state) => {
  return state.appointment.settingsActiveTab;
};
export const getAppointmentSettings = (state) => {
  return state.appointment.appointmentSettings;
};
export const getAppointmentSettingsDetails = (state) => {
  return state.appointment.appointmentSettings.details;
};
export const getCalendars = (state) => {
  return state.appointment.calendars;
};
export const getCalendarsCallState = (state) => {
  return state.appointment.isCalendarsFetched;
};
export const getCalendar = (state) => {
  return state.appointment.calendar;
};
export const getReponseHandled = (state) => {
  return state.appointment.reponseHandled;
};
export const getTempStatusPayload = (state) => {
  return state.appointment.tempStatusPayload;
};

// ── Persisted view selectors ──
export const getCalendarView = (state) => {
  return state.appointment.calendarView;
};
export const getSelectedServiceTypeIds = (state) => {
  return state.appointment.selectedServiceTypeIds;
};
export const getSelectedMemberIds = (state) => {
  return state.appointment.selectedMemberIds;
};
export const getUnsavedFormData = (state) => {
  return state.appointment.unsavedFormData;
};
export const getUnsavedLeadFormData = (state) => {
  return state.appointment.unsavedLeadFormData;
};
export const getSavedInfoWhenLeadCreate = (state) => {
  return state.appointment.savedInfoWhenLeadCreate;
};
export const getPendingAppointmentModal = (state) => {
  return state.appointment.pendingAppointmentModal;
};
export const getAppointmentModalShowNewLeadForm = (state) => {
  return state.appointment.appointmentModalShowNewLeadForm;
};
export const getAppointmentInlineLeadDraft = (state) => {
  return state.appointment.appointmentInlineLeadDraft;
};
export const getSettingsModalMembersSearch = (state) =>
  state.appointment.settingsModalMembersSearch;

// ── Appointment settings page-layout open/form selectors ──
export const getAppointmentSettingsOpen = (state) =>
  state.appointment.appointmentSettingsOpen;
export const getAppointmentSettingsEditData = (state) =>
  state.appointment.appointmentSettingsEditData;
export const getAppointmentSettingsFormDraft = (state) =>
  state.appointment.appointmentSettingsFormDraft;

export default AppointmentSlice.reducer;
